import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useServices } from '../../../contexts/ServicesContext';
import type { ServiceItem } from './types';

interface ServicesSectionProps {
  services?: ServiceItem[]; // Optional - sẽ load từ API nếu không có
  isVisible: boolean;
  sectionRef: React.RefObject<HTMLElement | null>;
  name?: string;
  description?: string;
  background?: string;
  nameColor?: string;
  descriptionColor?: string;
}

interface ApiService {
  _id: string;
  name: string;
  shortDescription?: string;
  description?: string;
  icon?: string;
  type: 'service' | 'solution' | 'academy';
  isActive?: boolean;
  isFeatured?: boolean;
  order?: number;
  parentId?: string | null;
  slug?: string;
  features?: string[];
  featuredImage?: string | null;
}

export default function ServicesSection({
  services: propServices,
  isVisible,
  sectionRef,
  name = 'Dịch Vụ Của Chúng Tôi',
  description = 'Giải pháp công nghệ toàn diện từ tư vấn đến triển khai và vận hành',
  background,
  nameColor,
  descriptionColor
}: ServicesSectionProps) {
  const { services: contextServices } = useServices();
  const [services, setServices] = useState<ServiceItem[]>(propServices || []);

  // Lấy data từ context (đã được Header fetch)
  useEffect(() => {
    // Lấy services (limit 5)
    const serviceItems = contextServices
      .sort((a: ApiService, b: ApiService) => (a.order || 0) - (b.order || 0))
      .slice(0, 5);
    
    if (serviceItems.length > 0) {
      const mappedServices: ServiceItem[] = serviceItems.map((item: ApiService) => ({
        icon: item.icon || 'ri-service-line',
        title: item.name,
        description: item.shortDescription || item.description || '',
        slug: item.slug,
      }));
      setServices(mappedServices);
    } else if (propServices && propServices.length > 0) {
      setServices(propServices);
    }
  }, [contextServices, propServices]);

  // Đổi chỗ "Tư vấn chuyển đổi số" (index 1) và "Security Assessment & Code Review" (index 3) cho đẹp
  const displayServices =
    services.length >= 4
      ? (() => {
          const next = [...services];
          [next[1], next[3]] = [next[3], next[1]];
          return next;
        })()
      : services;

  const backgroundStyle = background
    ? {
        backgroundImage: background.startsWith('http') || background.startsWith('/')
          ? `url(${background})`
          : undefined,
        backgroundColor: background.startsWith('http') || background.startsWith('/') ? undefined : background,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;


  return (
    <section 
      ref={sectionRef}
      data-section="services"
      className={`py-20 md:py-28 lg:py-32 relative overflow-hidden ${!background ? 'bg-white' : ''}`}
      style={backgroundStyle}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Services Section Header */}
        {(name || description) && (
          <div className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            {name && (
              <h2 
                className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-5 leading-[1.2] tracking-[-0.02em]"
                style={{ color: nameColor || '#2a4b7c' }}
              >
                {name}
              </h2>
            )}
            {description && (
              <p 
                className="text-base md:text-lg max-w-3xl mx-auto leading-[1.7] font-normal"
                style={{ color: descriptionColor || '#64748b' }}
              >
                {description}
              </p>
            )}
          </div>
        )}

        {/* Services Grid – 5 thẻ như CommonIssues: md:2 cột, lg:3 cột, thẻ thứ 4 (index 3) span 2 cột */}
        {services.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600">Chưa có dịch vụ nào</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {displayServices.map((service, index) => (
              <div
                key={index}
                className={`group relative bg-white rounded-xl md:rounded-2xl p-6 md:p-7 lg:p-8 border border-gray-200 hover:border-[#06b6d4] transition-all duration-500 hover:shadow-lg hover:-translate-y-2 min-w-0 ${
                  index === 3 ? 'lg:col-span-2 md:col-span-2' : ''
                } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mb-5 md:mb-6">
                  <i className={`${service.icon} text-4xl md:text-5xl text-[#06b6d4] group-hover:scale-110 transition-transform duration-300`} />
                </div>
                <h3 className="text-base md:text-lg lg:text-xl font-bold text-[#2a4b7c] mb-3 md:mb-4 leading-tight">
                  {service.title}
                </h3>
                <p className="text-sm md:text-base text-[#64748b] leading-relaxed mb-5 md:mb-6">
                  {service.description}
                </p>
                {service.slug && (
                  <Link
                    to={`/dich-vu/${service.slug}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#06b6d4] hover:text-[#0891b2] group/link transition-colors"
                  >
                    Tìm hiểu thêm
                    <i className="ri-arrow-right-line group-hover/link:translate-x-0.5 transition-transform" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

